import React from 'react';
import './index.css';
import './reset.css';
import Router from './Router';
import { AuthProvider } from './context/AuthContext';
import { LayoutProvider } from './context/LayoutContext';
import { ThemeProvider } from './theme/ThemeProvider';
import ThemeErrorBoundary from './theme/ThemeErrorBoundary';
import ThemeProviderSafety from './components/common/ThemeProviderSafety';
import { AccessibilityProvider } from './accessibility';
import { ThemeApplier } from './components/common/ThemeApplier';
import { LanguageProvider } from './context/LanguageContext';
import PWAInstallPrompt from './components/pwa/PWAInstallPrompt';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { CornerstoneProvider } from '@mb/platform-sdk/react';
import { cornerstoneClient } from './services/cornerstoneClient';

// Error boundary for Cornerstone SDK failures (ME-10)
class CornerstoneErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidCatch(error: Error) {
    console.error('Cornerstone SDK error:', error);
  }
  render() {
    if (this.state.hasError) {
      // Render children without Cornerstone — app degrades gracefully
      return this.props.children;
    }
    return (
      <CornerstoneProvider client={cornerstoneClient}>
        {this.props.children}
      </CornerstoneProvider>
    );
  }
}

/**
 * App Component
 *
 * The root component of the application.
 *
 * Provider Hierarchy:
 * 1. ThemeProviderSafety - Ensures theme is always available and valid
 * 2. ThemeProvider - Unified theme provider that handles both CSS variables and styled-components
 * 3. ThemeErrorBoundary - Catches and handles theme-related errors
 * 4. AuthProvider - Provides authentication state and functions
 * 5. SessionMonitor - Monitors session expiration and provides warnings
 * 6. LayoutProvider - Manages layout settings like content insets
 * 7. AccessibilityProvider - Applies accessibility preferences based on user settings
 * 8. Router - Handles routing (includes AIAgentProvider inside BrowserRouter)
 *
 * PWA Enhancement (Isolated):
 * - PWAInstallPrompt: Standalone overlay component for "Add to Home Screen" prompt
 * - Controlled by VITE_ENABLE_PWA_FEATURES flag (disabled by default)
 * - Does NOT modify existing responsive UI (MobileImpactDashboard, etc.)
 * - Can be instantly disabled via environment variable
 *
 * Cornerstone Platform Integration:
 * - QueryClientProvider: Required for @mb/platform-sdk React hooks
 * - CornerstoneProvider: Provides SDK client to all components via useCornerstoneClient()
 * - SDK SessionMonitor: Auto-checks session on init, window focus, and every 60s
 *   (configured in cornerstoneClient.ts, replaces app's own SessionMonitor)
 */

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes — matches Cornerstone SDK cache
      retry: 1,
    },
  },
});

const App: React.FC = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <CornerstoneErrorBoundary>
        <ThemeProviderSafety>
          <ThemeProvider>
            <ThemeErrorBoundary>
              <AuthProvider>
                <ThemeApplier>
                  <LanguageProvider>
                    <LayoutProvider>
                      <AccessibilityProvider>
                        <Router />
                        <PWAInstallPrompt />
                      </AccessibilityProvider>
                    </LayoutProvider>
                  </LanguageProvider>
                </ThemeApplier>
              </AuthProvider>
            </ThemeErrorBoundary>
          </ThemeProvider>
        </ThemeProviderSafety>
      </CornerstoneErrorBoundary>
    </QueryClientProvider>
  );
};

export default App;
